// react
import React, { useState, useEffect } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Box, SwipeableDrawer, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Button, IconButton, Typography, TextField, Tooltip, useTheme, useMediaQuery } from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";

// ui-components
import { Puller } from "ui-components/Puller";
import { LoadingDialog } from "ui-components/LoadingComponent";

// group-controller
import { addMember } from "group-controller/manageMember";

// user-controller
import getUUIDFromEmail from "user-controller/getUUIDFromEmail";

// A ---------------------------------------------------------------------- M

const AddMember = ({ group, open, setOpen, handleSuccessfulAdded, handleErrorAdded }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [userNotFound, setUserNotFound] = useState(false);
  const [userAlreadyAdded, setUserAlreadyAdded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handlePopstate = () => open && handleReset();
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open]);

  useEffect(() => {
    setUserNotFound(false);
    setUserAlreadyAdded(false);
  }, [newMemberEmail]);

  const handleReset = () => {
    setNewMemberEmail("");
    setUserNotFound(false);
    setUserAlreadyAdded(false);
    setOpen(false);
  };

  const handleAddMember = async () => {
    setIsLoading(true);

    try {
      const newMemberUID = await getUUIDFromEmail(newMemberEmail);

      if (!newMemberUID) {
        setUserNotFound(true);
        setIsLoading(false);
        return;
      }

      const result = await addMember(newMemberUID, group.id);

      switch (result.status) {
        case "success":
          handleReset();
          handleSuccessfulAdded();
          break;
        case "user_already_added":
          setUserAlreadyAdded(true);
          break;
        case "error":
        default:
          handleErrorAdded();
      }
    } catch (error) {
      console.error("Error adding member:", error);
      handleErrorAdded();
    } finally {
      setIsLoading(false);
    }
  };

  const innerContent = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          margin="dense"
          label={t("email")}
          variant="outlined"
          value={newMemberEmail}
          onChange={(e) => setNewMemberEmail(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              backgroundColor: "#ffffff",
            },
          }}
        />
      </Grid>

      {userNotFound && (
        <Grid item xs={12}>
          <Typography variant="body1" color="error">
            {t("member_error_1")}
          </Typography>
        </Grid>
      )}

      {userAlreadyAdded && (
        <Grid item xs={12}>
          <Typography variant="body1" color="error">
            {t("member_error_2")}
          </Typography>
        </Grid>
      )}
    </Grid>
  );

  if (isLoading) {
    return <LoadingDialog open={isLoading} />;
  }

  return isMobile ? (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={handleReset}
      onOpen={() => setOpen(true)}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          height: "90%",
          borderTopLeftRadius: "4%",
          borderTopRightRadius: "4%",
        },
      }}
    >
      <Puller />

      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} textAlign="center" mb={3}>
          {t("add_member")}
        </Typography>

        {innerContent}

        <Grid container spacing={2} mt={5}>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 1,
              }}
              onClick={handleAddMember}
              disabled={!newMemberEmail}
            >
              {t("confirm")}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="text"
              sx={{
                fontWeight: 600,
                textTransform: "none",
              }}
              onClick={handleReset}
            >
              {t("cancel")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </SwipeableDrawer>
  ) : (
    <Dialog
      open={open}
      onClose={handleReset}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          backdropFilter: "blur(10px)",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
          transition: "all 0.3s ease",
        },
      }}
    >
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6" fontWeight={700}>
              {t("add_member")}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={t("close")} placement="top">
              <IconButton onClick={handleReset} color="error" edge="end">
                <CloseOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>{innerContent}</DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          variant="text"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={handleReset}
        >
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={handleAddMember}
          disabled={!newMemberEmail}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMember;
